import { Component, OnInit, DestroyRef, inject } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { ActivatedRoute, Router } from '@angular/router'
import { combineLatest, tap } from 'rxjs'
import { TipoConcurso, TipoConcursoDTA } from 'src/app/enums/tipo-concurso'
import { Titulacao } from 'src/app/models/examinador-unicamp-consulta'
import { AuthService } from 'src/app/services/auth.service'
import { UnespCoreAuthService, UnespCoreMessageService } from 'src/libs/unesp-core'
import { ComissaoJulgadoraService } from '../comissao-julgadora-service'
import { DeclarationDialogOutput } from '../declaration/declaration.component'
import { AcordoDocumentStepperDialogComponent } from '../acordo-document-stepper-dialog/acordo-document-stepper-dialog.component'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

type Dados = {
  unidadeConcurso?: string
  tipoConcurso?: string
  tituloConcurso?: string
  dataProvaFase1?: string
  dataProvaFase2?: string
  emailContato?: string
  emailDepartamento?: string
  nomeExaminador?: string
  emailExaminador?: string
  matricula?: string
  unidade?: string
  titulacao?: string
  papelExaminador?: string
}

@Component({
  selector: 'inscricoes-registar-de-acordo-examinador',
  templateUrl: './registar-de-acordo-examinador.component.html',
  styleUrls: ['./registar-de-acordo-examinador.component.scss'],
})
export class RegistarDeAcordoExaminadorComponent implements OnInit {
  private destroyRef = inject(DestroyRef)

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly comissaoJulgadoraService: ComissaoJulgadoraService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly unespCoreAuthService: UnespCoreAuthService,
    private readonly unespCoreMessageService: UnespCoreMessageService,
    public dialog: MatDialog,
  ) {}

  comissaoJulgadoraId!: number
  token!: string
  dadosComissaoJulgadora: any = {}

  errorFetchingData = false

  concursoLink?: string

  public form = new FormGroup({
    unidadeConcurso: new FormControl(''),
    tipoConcurso: new FormControl(''),
    tituloConcurso: new FormControl(''),
    dataProvaFase1: new FormControl(''),
    dataProvaFase2: new FormControl(''),
    emailContato: new FormControl(''),
    emailDepartamento: new FormControl(''),
    nomeExaminador: new FormControl(''),
    emailExaminador: new FormControl(''),
    matricula: new FormControl(''),
    unidade: new FormControl(''),
    titulacao: new FormControl(''),
    papelExaminador: new FormControl(''),
  })

  ngOnInit(): void {
    this.form.disable()

    combineLatest({
      params: this.activatedRoute.params,
      queryParams: this.activatedRoute.queryParams,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ params, queryParams }) => {
      this.comissaoJulgadoraId = params['comissaoJulgadoraId']
      this.token = queryParams['token']

      if (
        (queryParams['confirmed'] === true || queryParams['confirmed'] === 'true') &&
        queryParams['data'] &&
        this.isLoggedIn()
      ) {
        const data = queryParams['data']
        const parsed = JSON.parse(atob(data)) as DeclarationDialogOutput

        this.registrarDeAcordo(parsed)
      }

      this.fetchData()
    })
  }

  fetchData() {
    this.errorFetchingData = false
    this.comissaoJulgadoraService
      .getDadosRegistrarDeAcordoExaminador({
        comissaoJulgadoraId: this.comissaoJulgadoraId,
        token: this.token,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          this.dadosComissaoJulgadora = data
          this.populateForm(data)
        },
        error: error => {
          this.errorFetchingData = true
        },
      })
  }

  populateForm(dados: any) {
    this.form.patchValue({
      unidadeConcurso: dados.unidadeConcurso,
      tipoConcurso: this.getTipoConcurso(dados.tipoConcurso),
      tituloConcurso: dados.tituloConcurso,
      dataProvaFase1: dados.dataProvaFase1,
      dataProvaFase2: dados.dataProvaFase2,
      emailContato: dados.emailContato,
      emailDepartamento: dados.emailDepartamento,
      nomeExaminador: dados.nomeExaminador,
      emailExaminador: dados.emailExaminador,
      matricula: dados.matriucla,
      unidade: dados.unidade,
      titulacao: this.getTitulacao(dados.titulacao),
      papelExaminador: dados.papelExaminador,
    })

    this.concursoLink = dados.concursoLink
  }

  openDialog() {
    const dialogRef = this.dialog.open(AcordoDocumentStepperDialogComponent, {
      data: { comissaoJulgadoraId: this.comissaoJulgadoraId, token: this.token },
      height: '90vh',
      panelClass: 'fullheight-dialog',
      disableClose: true,
    })

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.fetchData()
    })
    
  }

  fechar() {
    this.authService.login(this.router.url)
  }

  voltar() {
    this.router.navigate(['/concurso'])
  }

  registrarDeAcordo(dadosDeAcordo: DeclarationDialogOutput) {
    this.comissaoJulgadoraService
      .postRegistrarDeAcordoExaminador({
        ...dadosDeAcordo,
      })
      .pipe(tap(() => this.unespCoreMessageService.showMessageSuccess('De Acordo registrado com sucesso!')))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.router.navigate(['/']))
  }

  showTipoConcursoDTA(): boolean {
    return (
      !this.unespCoreAuthService.hasPermissionBasedRoles(['RH']) &&
      this.unespCoreAuthService.hasPermissionBasedRoles(['DTA'])
    )
  }

  getTipoConcurso(tipo: string): string {
    const tipoConcursoEnum = this.showTipoConcursoDTA() ? TipoConcursoDTA : TipoConcurso
    return tipoConcursoEnum[tipo as keyof typeof tipoConcursoEnum] || tipo
  }

  getTitulacao(titulacao: Titulacao) {
    return Titulacao[titulacao]
  }

  isLoggedIn(): boolean {
    return this.authService.isLoggedIn
  }
}
